import { useAuthState } from '@auth/hooks';
import type { Community } from '@communities/community/domain';
import { routes as communityRoutes } from '@communities/routes';
import { HeroImage } from '@core/components';
import { UserRoles, FEATURE_FLAGS } from '@core/domain';
import { useIsFeatureEnabled } from '@core/hooks';
import { withAppInsightsTracking } from '@core/logging/withAppInsightsTracking';
import { routes as auditRoutes } from '@internal-audit/routes';
import { Heading, Button, ButtonType, OverlayMode, IconButton, Delete, IconButtonSize, IconSize } from '@soteria/design-system-ve';
import { useNavigate } from 'react-router-dom';

import * as styles from './CommunityHeaderSection.css.ts';

/**
 * Build the subheader text with community group, content rating, and genres
 */
function buildSubheaderText(community: Community): string {
  const subheaderParts: string[] = [];

  if (community.communityGroup?.name) {
    subheaderParts.push(community.communityGroup.name);
  }

  if (community.optionalMetadata?.contentRating?.description) {
    subheaderParts.push(community.optionalMetadata.contentRating.description);
  }

  if (community.optionalMetadata?.contentGenres && community.optionalMetadata.contentGenres.length > 0) {
    subheaderParts.push(community.optionalMetadata.contentGenres.join(', '));
  }

  return subheaderParts.join(' • ');
}

interface CommunityHeaderSectionProps {
  community: Community;
  onDeleteCommunity?: () => void;
}

/**
 * Header section component with gradient transition
 * Contains community name, description, metadata, and action buttons
 * Background image is handled by the global useHeroBackground hook
 */

function CommunityHeaderSectionComponent({ community, onDeleteCommunity }: CommunityHeaderSectionProps) {
  const navigate = useNavigate();
  const { hasRoleAccess } = useAuthState();

  const { name } = community;

  const hasQAAccess = hasRoleAccess(UserRoles.QA, community.communityGroupId, community.id);
  const isModerationEnabled = useIsFeatureEnabled(FEATURE_FLAGS.MODERATION);
  const hasModerationAccess =
    hasRoleAccess(UserRoles.MODERATOR, community.communityGroupId, community.id) || hasRoleAccess(UserRoles.ADMIN, community.communityGroupId, community.id);

  const subheaderText = buildSubheaderText(community);

  const onTestClassification = () => {
    navigate(communityRoutes.communities.classificationTesting.root(community.id));
  };

  const onViewAudits = () => {
    navigate(auditRoutes.internalAudit.community.audits(community.id));
  };

  const onSearchContent = () => {
    navigate(communityRoutes.communities.contentSearch.root(community.id));
  };

  const onModeration = () => {
    navigate(`/communities/${community.id}/moderation`);
  };

  return (
    <div className={styles.heroSection}>
      {/* Gradient Overlay */}
      <div className={styles.heroOverlay} />

      <div className={styles.heroContent}>
        {/* Left Column - Image */}
        <HeroImage logoUrl={community.logoUrl} name={name} />

        {/* Right Column - Content */}
        <div className={styles.heroContentRight}>
          {/* Title and Subheader Container */}
          <div>
            {/* Community Title */}
            <Heading className={styles.communityTitle} level="h1">
              {name}
            </Heading>

            {/* Community Subheader with Group, Rating, and Genres */}
            {subheaderText && <div className={styles.communitySubheader}>{subheaderText}</div>}
          </div>

          {/* Action Buttons */}
          <div className={styles.actionsContainer}>
            {/* Test Classification Button */}
            <Button buttonType={ButtonType.Primary} overlayMode={OverlayMode.Solid} onClick={onTestClassification}>
              TEST CLASSIFICATION
            </Button>

            {/* View Audits Button - Only show to users with QA access*/}
            {hasQAAccess && (
              <Button buttonType={ButtonType.Primary} overlayMode={OverlayMode.Solid} onClick={onViewAudits}>
                VIEW AUDITS
              </Button>
            )}

            {/* Search Content Button */}
            <Button buttonType={ButtonType.Primary} overlayMode={OverlayMode.Solid} onClick={onSearchContent}>
              SEARCH CONTENT
            </Button>

            {/* Moderation Button - Only show when feature flag is enabled and user has moderation permissions */}
            {isModerationEnabled && hasModerationAccess && (
              <Button buttonType={ButtonType.Primary} overlayMode={OverlayMode.Solid} onClick={onModeration}>
                MODERATION
              </Button>
            )}

            {/* Delete Community Button */}
            {onDeleteCommunity && (
              <IconButton
                aria-label="Delete Community"
                buttonSize={IconButtonSize.XLarge}
                buttonType={ButtonType.Danger}
                icon={Delete}
                iconSize={IconSize.XSmall}
                overlayMode={OverlayMode.Solid}
                onClick={onDeleteCommunity}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export const CommunityHeaderSection = withAppInsightsTracking(CommunityHeaderSectionComponent, 'CommunityHeaderSection');
